import { supabase } from "./supabase/client";

/**
 * Activity Log Categories
 */
export type ActivityCategory =
  | "auth"
  | "profile"
  | "admin"
  | "weekend"
  | "team"
  | "score"
  | "game"
  | "agenda"
  | "notification"
  | "photo";

/**
 * Activity Log Action Types
 */
export type ActivityActionType =
  // Auth actions
  | "auth.login"
  | "auth.logout"
  | "auth.signup"
  | "auth.password_reset"
  // Profile actions
  | "profile.update"
  | "profile.avatar_change"
  // Admin actions
  | "admin.role_assign"
  | "admin.role_revoke"
  | "admin.user_block"
  | "admin.user_unblock"
  | "admin.user_delete"
  // Weekend actions
  | "weekend.create"
  | "weekend.update"
  | "weekend.activate"
  | "weekend.deactivate"
  | "weekend.finalize"
  | "weekend.unfinalize"
  | "weekend.delete"
  // Team actions
  | "team.create"
  | "team.update"
  | "team.delete"
  | "team.member_add"
  | "team.member_remove"
  // Score actions
  | "score.award"
  | "score.deduct"
  // Game actions
  | "game.create"
  | "game.update"
  | "game.start"
  | "game.pause"
  | "game.end"
  | "game.delete"
  // Agenda actions
  | "agenda.create"
  | "agenda.update"
  | "agenda.delete"
  | "agenda.reveal"
  // Challenge actions
  | "challenge.create"
  | "challenge.update"
  | "challenge.delete"
  | "challenge.activate"
  | "challenge.pause"
  | "challenge.complete"
  | "challenge.submit"
  | "challenge.approve"
  | "challenge.reject"
  // Speurtocht actions
  | "speurtocht.create"
  | "speurtocht.update"
  | "speurtocht.delete"
  | "speurtocht.draft"
  | "speurtocht.active"
  | "speurtocht.paused"
  | "speurtocht.completed"
  | "speurtocht.archived"
  | "speurtocht.start"
  | "speurtocht.step_complete"
  // Memory game actions
  | "memory.complete"
  | "memory.versus_complete"
  // Notification actions
  | "notification.broadcast"
  // Photo actions
  | "photo.upload"
  | "photo.delete"
  | "photo.like"
  | "photo.comment"
  | "photo.feature"
  | "photo.hide";

/**
 * Target types for activity logs
 */
export type ActivityTargetType =
  | "user"
  | "weekend"
  | "team"
  | "game"
  | "game_session"
  | "role"
  | "agenda_event"
  | "challenge"
  | "challenge_submission"
  | "speurtocht"
  | "speurtocht_session"
  | "speurtocht_step"
  | "notification"
  | "photo";

/**
 * Activity log entry interface
 */
export interface ActivityLogEntry {
  user_id: string;
  action_type: ActivityActionType;
  action_category: ActivityCategory;
  target_type?: ActivityTargetType;
  target_id?: string;
  target_name?: string;
  metadata?: Record<string, unknown>;
  weekend_id?: string;
}

/**
 * Log an activity to the activity_logs table
 * Uses a SECURITY DEFINER RPC function to bypass RLS during signup
 * @param entry - The activity log entry to create
 * @returns Promise with the result of the insert operation
 */
export const logActivity = async (
  entry: ActivityLogEntry
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.rpc("insert_activity_log", {
      p_user_id: entry.user_id,
      p_action_type: entry.action_type,
      p_action_category: entry.action_category,
      p_target_type: entry.target_type || null,
      p_target_id: entry.target_id || null,
      p_target_name: entry.target_name || null,
      p_metadata: entry.metadata || {},
      p_weekend_id: entry.weekend_id || null,
    });

    if (error) {
      console.error("Failed to log activity:", error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error("Error logging activity:", err);
    return { success: false, error: "Failed to log activity" };
  }
};

/**
 * Helper to get the category from an action type
 */
export const getCategoryFromAction = (
  actionType: ActivityActionType
): ActivityCategory => {
  const prefix = actionType.split(".")[0];
  return prefix as ActivityCategory;
};

/**
 * Helper to create a role change log entry
 */
export const logRoleChange = async (
  performedByUserId: string,
  targetUserId: string,
  targetUserName: string,
  role: string,
  action: "assign" | "revoke",
  weekendId?: string
): Promise<void> => {
  await logActivity({
    user_id: performedByUserId,
    action_type: action === "assign" ? "admin.role_assign" : "admin.role_revoke",
    action_category: "admin",
    target_type: "user",
    target_id: targetUserId,
    target_name: targetUserName,
    metadata: { role, weekend_id: weekendId },
    weekend_id: weekendId,
  });
};

/**
 * Helper to create a weekend action log entry
 */
export const logWeekendAction = async (
  userId: string,
  weekendId: string,
  weekendName: string,
  action: "create" | "update" | "activate" | "deactivate" | "finalize" | "unfinalize" | "delete",
  metadata?: Record<string, unknown>
): Promise<void> => {
  await logActivity({
    user_id: userId,
    action_type: `weekend.${action}` as ActivityActionType,
    action_category: "weekend",
    target_type: "weekend",
    target_id: weekendId,
    target_name: weekendName,
    metadata,
    weekend_id: weekendId,
  });
};

/**
 * Helper to create a user management log entry
 */
export const logUserAction = async (
  performedByUserId: string,
  targetUserId: string,
  targetUserName: string,
  action: "block" | "unblock" | "delete",
  metadata?: Record<string, unknown>
): Promise<void> => {
  await logActivity({
    user_id: performedByUserId,
    action_type: `admin.user_${action}` as ActivityActionType,
    action_category: "admin",
    target_type: "user",
    target_id: targetUserId,
    target_name: targetUserName,
    metadata,
  });
};

/**
 * Helper to create an auth log entry
 */
export const logAuthAction = async (
  userId: string,
  action: "login" | "logout" | "signup" | "password_reset",
  metadata?: Record<string, unknown>
): Promise<void> => {
  await logActivity({
    user_id: userId,
    action_type: `auth.${action}` as ActivityActionType,
    action_category: "auth",
    metadata,
  });
};

/**
 * Helper to create a challenge action log entry
 */
export const logChallengeAction = async (
  userId: string,
  challengeId: string,
  challengeTitle: string,
  action: "create" | "update" | "delete" | "activate" | "pause" | "complete" | "submit" | "approve" | "reject",
  metadata?: Record<string, unknown>,
  weekendId?: string
): Promise<void> => {
  await logActivity({
    user_id: userId,
    action_type: `challenge.${action}` as ActivityActionType,
    action_category: "game",
    target_type: action === "submit" || action === "approve" || action === "reject" ? "challenge_submission" : "challenge",
    target_id: challengeId,
    target_name: challengeTitle,
    metadata,
    weekend_id: weekendId,
  });
};

/**
 * Helper to create a speurtocht action log entry
 */
export const logSpeurttochtAction = async (
  userId: string,
  speurttochtId: string,
  speurttochtName: string,
  action: "create" | "update" | "delete" | "draft" | "active" | "paused" | "completed" | "archived" | "start" | "step_complete",
  metadata?: Record<string, unknown>,
  weekendId?: string
): Promise<void> => {
  await logActivity({
    user_id: userId,
    action_type: `speurtocht.${action}` as ActivityActionType,
    action_category: "game",
    target_type: action === "step_complete" ? "speurtocht_step" : action === "start" ? "speurtocht_session" : "speurtocht",
    target_id: speurttochtId,
    target_name: speurttochtName,
    metadata,
    weekend_id: weekendId,
  });
};

